import { useTranslation } from 'react-i18next';
import { useSession } from '@/auth/session';
import { PERMISSIONS } from '@/auth/permissions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeadStockTab } from '@/features/reports/DeadStockTab';
import { ExpiryBandsTab } from '@/features/reports/ExpiryBandsTab';
import { GstSummaryTab } from '@/features/reports/GstSummaryTab';
import { SalesAnalyticsTab } from '@/features/reports/SalesAnalyticsTab';

/**
 * Tabs are filtered to what the signed-in user can actually see — the route
 * itself only requires reports.sales, so a user without reports.financials
 * should never even see a GST tab that would 403 when clicked.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const { can } = useSession();

  const tabs = [
    { value: 'sales', label: t('salesTitle'), show: can(PERMISSIONS.reportsSales), content: <SalesAnalyticsTab /> },
    { value: 'dead-stock', label: 'Dead stock', show: can(PERMISSIONS.reportsExpiry), content: <DeadStockTab /> },
    { value: 'expiry', label: 'Expiry bands', show: can(PERMISSIONS.reportsExpiry), content: <ExpiryBandsTab /> },
    { value: 'gst', label: 'GST summary', show: can(PERMISSIONS.reportsFinancials), content: <GstSummaryTab /> },
  ].filter((tab) => tab.show);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-3xl">Reports</h1>
        <p className="text-sm text-muted-foreground">Sales trends, stagnant stock, and expiry risk.</p>
      </div>

      {tabs.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          You don't have access to any reports yet.
        </p>
      ) : (
        <Tabs defaultValue={tabs[0]!.value}>
          <TabsList>
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
